import { LogCategory } from "Enumerations";
import { SearchResultItem } from "Shared/Components";
import { useLogging } from "Shared/Hooks";
import { useGlobalSetting } from "Store/GlobalSettingStore";
import { useRootStore } from "Store/RootStore";
import { universalSearchResultItem, allCategories } from "Store/UniversalSearchStore";
import { maxShownHistoryCount } from "Store/UserSearchSettings";
import { SearchItem } from "Utils/universalSearchUtils";

export function useSearchResultList({
    trendingSearchesData,
    setValue,
    filterItem
}: {
    trendingSearchesData: universalSearchResultItem[];
    setValue: (value: string) => void;
    filterItem: SearchResultItem;
}): SearchResultItem[] {
    const searchStore = useRootStore("UniversalSearchStore");
    const userSearchSettings = useGlobalSetting("userSearchSettings");
    const { trackEvent } = useLogging(LogCategory.Core);

    if (!searchStore.isGlobalSearch && searchStore.hasTable && searchStore.searchWord === "") {
        return [filterItem];
    }

    if (searchStore.searchWord === "") {
        const historyItems: universalSearchResultItem[] = userSearchSettings.getAllItems().slice(0, maxShownHistoryCount);
        let suggestionResults: SearchResultItem[] = historyItems.map((item) => ({
            ...item,
            onClick: () => {
                setTimeout(() => {
                    trackEvent("cfb298bf-edc4-4590-99db-00e5bdafe5e6", "UniversalSearchResultSelection", {
                        searchWord: searchStore.searchWord,
                        resultItemTitle: item.title,
                        newPath: window.location.pathname,
                        description: item.description,
                        category: item.originalCategory ? item.originalCategory : "Recent Searches"
                    });
                }, 0);
            },
            deleteItemFromHistory: () => userSearchSettings.removeItem(item.originalCategory ? item.originalCategory : "Recent Searches", item.id)
        }));

        if (trendingSearchesData.length > 0) {
            const trendingItems: SearchResultItem[] = trendingSearchesData.map((item) => ({
                ...item,
                onClick: () => {
                    setTimeout(() => {
                        trackEvent("cfb298bf-edc4-4590-99db-00e5bdafe5e6", "UniversalSearchResultSelection", {
                            searchWord: searchStore.searchWord,
                            resultItemTitle: item.title,
                            newPath: window.location.pathname,
                            description: item.description,
                            category: item.originalCategory ? item.originalCategory : "Trending Searches"
                        });
                    }, 0);
                }
            }));
            suggestionResults = [...suggestionResults, ...trendingItems];
        }
        return searchStore.hasTable ? [filterItem, ...suggestionResults] : suggestionResults;
    }

    if (
        searchStore.filters.length > 0 &&
        searchStore.searchWord.trim() === `is:${searchStore.filters[0]}` &&
        searchStore.filters[0] &&
        allCategories.has(searchStore.filters[0])
    ) {
        const historyItems: universalSearchResultItem[] = userSearchSettings.getAllItems();
        const categorySuggestions = historyItems
            .filter((item) => item.originalCategory === searchStore.filters[0])
            .map((item) => ({
                ...item,
                onClick: () => {
                    setTimeout(() => {
                        trackEvent("cfb298bf-edc4-4590-99db-00e5bdafe5e6", "UniversalSearchResultSelection", {
                            searchWord: searchStore.searchWord,
                            resultItemTitle: item.title,
                            newPath: window.location.pathname,
                            description: item.description,
                            category: item.originalCategory ? item.originalCategory : "Recent Searches"
                        });
                    }, 0);
                },
                deleteItemFromHistory: () => userSearchSettings.removeItem(item.originalCategory ? item.originalCategory : "Recent Searches", item.id)
            }))
            .slice(0, maxShownHistoryCount);
        return searchStore.hasTable ? [filterItem, ...categorySuggestions] : categorySuggestions;
    }

    let results: SearchResultItem[] = searchStore.resultList.map((item: SearchItem) => {
        if (item.onClick || item.category === "Recent Searches") {
            return {
                ...item,
                onClick: () => {
                    item.onClick?.();
                    setValue(searchStore.searchWord);
                    const currentSearchWord = searchStore.searchWord;
                    if (item.category !== "Filters") {
                        userSearchSettings.addItem(item);
                        setTimeout(() => {
                            trackEvent("cfb298bf-edc4-4590-99db-00e5bdafe5e6", "UniversalSearchResultSelection", {
                                searchWord: currentSearchWord,
                                resultItemTitle: item.title,
                                newPath: window.location.pathname,
                                description: item.description,
                                category: item.originalCategory ? item.originalCategory : item.category
                            });
                        }, 0);
                    }
                }
            };
        }
        return item;
    });
    if (searchStore.hasTable) {
        results = [filterItem, ...results];
    }
    return results;
}
